/**
 * Column picker: show / hide and reorder table columns (drag a row, or the
 * ↑ ↓ buttons / Alt+Arrow keys), reset to the default set, and optionally
 * choose row density. Shared by Play Archive (`pages/archive/column-picker`)
 * and the desk `DataTable`; the choice is saved per logged-in user by the
 * caller (`useTableColumnPrefs` / Archive's `useColumnPrefs`).
 *
 * Columns that are visible by preference but do not fit the current width are
 * marked "no room" — they are still one tap away in the row detail.
 */
import { useRef, useState } from 'react';

import { Popover } from './popover';
import { moveColumn, moveColumnTo, toggleColumn, type ColumnPrefs } from './table-layout';

export interface PickerColumn {
  id: string;
  /** Full name shown in the picker. */
  title: string;
  /** Small second line (e.g. the column's group). */
  groupLabel?: string;
  /** Always shown, cannot be hidden or dragged. */
  isPinned?: boolean;
}

export interface ColumnPickerProps {
  columns: readonly PickerColumn[];
  prefs: ColumnPrefs;
  onChange: (next: ColumnPrefs) => void;
  /** Ids visible by preference but dropped for width right now. */
  hiddenByWidth: ReadonlySet<string>;
  isSavedLocallyOnly: boolean;
  /** Controlled open state. */
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  /** What "Reset to default" restores. */
  defaults: ColumnPrefs;
  /** Trigger button class (Archive: `archive-tool`; desk tables: `dt-tool`). */
  triggerClassName?: string;
  countClassName?: string;
  /** Show the Compact / Comfortable toggle. */
  hasDensity?: boolean;
}

export function ColumnPicker({
  columns,
  prefs,
  onChange,
  hiddenByWidth,
  isSavedLocallyOnly,
  isOpen,
  onOpenChange: setIsOpen,
  triggerClassName = 'archive-tool',
  countClassName = 'archive-tool__count',
  hasDensity = true,
  defaults,
}: ColumnPickerProps): JSX.Element {
  const [dragId, setDragId] = useState<string | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const byId = new Map(columns.map((column) => [column.id, column]));
  const visibleCount = prefs.columns.filter((entry) => entry.visible).length;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={triggerClassName}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        data-testid="column-picker-trigger"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span aria-hidden="true">▥</span> Columns
        {hiddenByWidth.size > 0 ? (
          <span className={countClassName} title="Visible columns without room at this width">
            {visibleCount - hiddenByWidth.size}/{visibleCount}
          </span>
        ) : null}
      </button>
      <Popover
        anchorRef={triggerRef}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        label="Columns"
        className="colpicker"
        width={340}
        align="end"
        testId="column-picker"
      >
        <div className="colpicker__head">
          <span className="colmenu__title">Columns</span>
          {hasDensity ? (
            <div className="colpicker__density" role="group" aria-label="Row density">
              {(['compact', 'comfortable'] as const).map((density) => (
                <button
                  key={density}
                  type="button"
                  aria-pressed={prefs.density === density}
                  onClick={() => onChange({ ...prefs, density })}
                >
                  {density === 'compact' ? 'Compact' : 'Comfortable'}
                </button>
              ))}
            </div>
          ) : null}
        </div>
        <p className="colpicker__hint">
          Drag to reorder, or use ↑ ↓. Saved to your account.
          {isSavedLocallyOnly ? ' (Offline — saved on this device for now.)' : ''}
        </p>
        <ol className="colpicker__list" aria-label="Column order">
          {prefs.columns.map((entry, index) => {
            const column = byId.get(entry.id);
            if (column === undefined) return null;
            const isPinned = column.isPinned === true;
            return (
              <li
                key={entry.id}
                className={`colpicker__row${dragId === entry.id ? ' colpicker__row--dragging' : ''}${
                  overIndex === index && dragId !== null && dragId !== entry.id
                    ? ' colpicker__row--over'
                    : ''
                }`}
                draggable={!isPinned}
                data-testid={`picker-row-${entry.id}`}
                onDragStart={(event) => {
                  setDragId(entry.id);
                  event.dataTransfer.effectAllowed = 'move';
                  event.dataTransfer.setData('text/plain', entry.id);
                }}
                onDragOver={(event) => {
                  if (dragId === null) return;
                  event.preventDefault();
                  setOverIndex(index);
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  if (dragId !== null) onChange(moveColumnTo(prefs, dragId, index));
                  setDragId(null);
                  setOverIndex(null);
                }}
                onDragEnd={() => {
                  setDragId(null);
                  setOverIndex(null);
                }}
              >
                <span className="colpicker__grip" aria-hidden="true">
                  {isPinned ? '' : '⠿'}
                </span>
                <label className="colpicker__label">
                  <input
                    type="checkbox"
                    checked={entry.visible}
                    disabled={isPinned}
                    onChange={() => onChange(toggleColumn(prefs, entry.id))}
                    onKeyDown={(event) => {
                      if (!event.altKey) return;
                      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
                        event.preventDefault();
                        onChange(moveColumn(prefs, entry.id, event.key === 'ArrowUp' ? -1 : 1));
                      }
                    }}
                  />
                  <span className="colpicker__name">
                    {column.title}
                    {column.groupLabel === undefined ? null : (
                      <span className="colpicker__group">{column.groupLabel}</span>
                    )}
                  </span>
                </label>
                {entry.visible && hiddenByWidth.has(entry.id) ? (
                  <span
                    className="colpicker__noroom"
                    title="Not enough width — shown when you expand a row"
                  >
                    no room
                  </span>
                ) : null}
                <span className="colpicker__move">
                  <button
                    type="button"
                    aria-label={`Move ${column.title} up`}
                    disabled={index === 0}
                    onClick={() => onChange(moveColumn(prefs, entry.id, -1))}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    aria-label={`Move ${column.title} down`}
                    disabled={index === prefs.columns.length - 1}
                    onClick={() => onChange(moveColumn(prefs, entry.id, 1))}
                  >
                    ↓
                  </button>
                </span>
              </li>
            );
          })}
        </ol>
        <div className="colpicker__foot">
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => onChange(defaults)}
          >
            Reset to default
          </button>
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            onClick={() => setIsOpen(false)}
          >
            Done
          </button>
        </div>
      </Popover>
    </>
  );
}
